import { useState } from "react";

import { Loader2, Plus, RefreshCw, Trash2, Key, X, Copy, Check, FolderKey } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EndpointCommandPicker } from "@/features/users/components/EndpointCommandPicker";
import { PermissionGroupsDialog } from "@/features/users/components/PermissionGroupsDialog";
import {
  useApiKeysByUser,
  useCreateApiKey,
  useDeleteApiKey,
  useRegenerateApiKey,
  useUpdateApiKey,
} from "@/features/users/hooks/useApiKeys";
import { useEndpointsConfig } from "@/features/users/hooks/useEndpointsConfig";
import type { ApiKey } from "@/features/users/schemas/user";
import type { EndpointDef } from "@/features/users/data/endpoints";
import { getEndpointById } from "@/features/users/utils/getEndpointById";
import { validateApiKeyPayload } from "@/features/users/utils/validateApiKeyPayload";

function warnAboutNoAuthScopes(scopeIds: string[], endpoints: EndpointDef[]): void {
  for (const id of scopeIds) {
    const ep = getEndpointById(id, endpoints);
    const label = ep?.label ?? id;
    console.warn(`Scope sin auth en la API Key: ${label} (${id})`);
    toast.warning(`Endpoint sin auth: ${label}`, {
      description: id,
    });
  }
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface ApiKeysDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onClose: () => void;
}

export function ApiKeysDialog({ userId, userName, open, onClose }: ApiKeysDialogProps) {
  const { data: apiKeys, isLoading } = useApiKeysByUser(userId);
  const { data: endpoints = [] } = useEndpointsConfig();
  const createApiKey = useCreateApiKey();
  const updateApiKey = useUpdateApiKey();
  const regenerateApiKey = useRegenerateApiKey();
  const deleteApiKey = useDeleteApiKey();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedEndpoints, setSelectedEndpoints] = useState<string[]>([]);
  const [editSelectedEndpoints, setEditSelectedEndpoints] = useState<string[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showGroupsDialog, setShowGroupsDialog] = useState(false);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;

    const { warnings } = validateApiKeyPayload(selectedEndpoints, endpoints);
    warnAboutNoAuthScopes(warnings, endpoints);

    try {
      const result = await createApiKey.mutateAsync({
        name: newKeyName,
        permissions: selectedEndpoints,
        user: userId,
      });

      setNewlyCreatedKey(result.value);
      setShowCreateForm(false);
      setNewKeyName("");
      setSelectedEndpoints([]);
    } catch (error) {
      console.error("Error creating API key:", error);
    }
  };

  const handleUpdate = async () => {
    if (!editingKey) return;

    const { warnings } = validateApiKeyPayload(editSelectedEndpoints, endpoints);
    warnAboutNoAuthScopes(warnings, endpoints);

    try {
      await updateApiKey.mutateAsync({
        id: editingKey.id,
        data: { permissions: editSelectedEndpoints },
      });

      setEditingKey(null);
      setEditSelectedEndpoints([]);
    } catch (error) {
      console.error("Error updating API key:", error);
    }
  };

  const handleRegenerate = async (keyId: string) => {
    try {
      const result = await regenerateApiKey.mutateAsync(keyId);
      setNewlyCreatedKey(result.value);
    } catch (error) {
      console.error("Error regenerating API key:", error);
    }
  };

  const handleDelete = async (keyId: string) => {
    try {
      await deleteApiKey.mutateAsync(keyId);
    } catch (error) {
      console.error("Error deleting API key:", error);
    }
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(value);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleClose = () => {
    setShowCreateForm(false);
    setEditingKey(null);
    setNewKeyName("");
    setSelectedEndpoints([]);
    setEditSelectedEndpoints([]);
    setNewlyCreatedKey(null);
    onClose();
  };

  const startEdit = (key: ApiKey) => {
    setEditingKey(key);
    setEditSelectedEndpoints([...key.permissions]);
  };

  const getEndpointLabel = (permId: string) => {
    const ep = getEndpointById(permId, endpoints);
    return ep ? ep.label : permId;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Key className="h-6 w-6 text-primary" />
            API Keys
          </DialogTitle>
          <DialogDescription>
            Gestiona las API Keys de {userName}. Podés aplicar un grupo o elegir
            endpoints individuales.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {!showCreateForm && !editingKey && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowGroupsDialog(true)}>
                <FolderKey className="h-4 w-4 mr-2" />
                Gestionar grupos
              </Button>
            </div>
          )}

          {newlyCreatedKey && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium text-green-800">Nueva API Key creada</p>
                  <p className="text-sm text-green-700 font-mono break-all">
                    {newlyCreatedKey}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                >
                  {copiedKey === newlyCreatedKey ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Copia esta clave ahora. No podrás verla nuevamente.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setNewlyCreatedKey(null)}
              >
                Entendido
              </Button>
            </div>
          )}

          {/* Create form */}
          {showCreateForm && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Nueva API Key</Label>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateForm(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="keyName">Nombre</Label>
                <Input
                  id="keyName"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Ej: Desarrollo, Producción"
                />
              </div>

              <div className="space-y-2">
                <Label>Endpoints autorizados</Label>
                <p className="text-xs text-muted-foreground">
                  Elegí un grupo rápido o ajustá endpoints uno a uno.
                </p>
                <EndpointCommandPicker
                  value={selectedEndpoints}
                  onChange={setSelectedEndpoints}
                  compact
                />
                <p className="text-xs text-muted-foreground">
                  {selectedEndpoints.length} endpoint(s) seleccionado(s)
                </p>
              </div>

              <Button
                onClick={handleCreate}
                disabled={
                  createApiKey.isPending ||
                  !newKeyName.trim() ||
                  selectedEndpoints.length === 0
                }
              >
                {createApiKey.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Crear API Key
              </Button>
            </div>
          )}

          {/* Edit form */}
          {editingKey && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Editar: {editingKey.name}</Label>
                <Button variant="ghost" size="icon" onClick={() => setEditingKey(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Endpoints autorizados</Label>
                <p className="text-xs text-muted-foreground">
                  Elegí un grupo rápido o ajustá endpoints uno a uno.
                </p>
                <EndpointCommandPicker
                  value={editSelectedEndpoints}
                  onChange={setEditSelectedEndpoints}
                  compact
                />
                <p className="text-xs text-muted-foreground">
                  {editSelectedEndpoints.length} endpoint(s) seleccionado(s)
                </p>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleUpdate} disabled={updateApiKey.isPending}>
                  {updateApiKey.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Guardar
                </Button>
                <Button variant="outline" onClick={() => setEditingKey(null)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* New key button */}
          {!showCreateForm && !editingKey && !isLoading && (
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva API Key
            </Button>
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys && apiKeys.length > 0 ? (
            <div className="space-y-2">
              {apiKeys.map((key) => (
                <div key={key.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{key.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Creada: {new Date(key.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Regenerar"
                        onClick={() => handleRegenerate(key.id)}
                        disabled={regenerateApiKey.isPending}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Eliminar"
                        onClick={() => handleDelete(key.id)}
                        disabled={deleteApiKey.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  {key.permissions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {key.permissions.slice(0, 4).map((perm) => (
                        <span
                          key={perm}
                          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                          title={getEndpointLabel(perm)}
                        >
                          {getEndpointLabel(perm)}
                        </span>
                      ))}
                      {key.permissions.length > 4 && (
                        <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          +{key.permissions.length - 4} más
                        </span>
                      )}
                    </div>
                  )}

                  <Button variant="link" size="sm" onClick={() => startEdit(key)}>
                    Editar permisos
                  </Button>
                </div>
              ))}
            </div>
          ) : !showCreateForm ? (
            <p className="text-center text-muted-foreground py-8">
              No hay API Keys para este usuario
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <PermissionGroupsDialog
      open={showGroupsDialog}
      onClose={() => setShowGroupsDialog(false)}
    />
    </>
  );
}
